/**
 * Timesheet Business Rules
 * 
 * Contains core business logic for timesheet calculations and validations.
 * Following Clean Architecture principles - business rules are isolated from
 * technical concerns and data structures.
 */

/**
 * Calculates gross hours worked without any deductions
 * @param timeIn Start time in HH:MM format (24-hour)
 * @param timeOut End time in HH:MM format (24-hour) 
 * @returns Gross hours worked (may span midnight)
 */
export const calculateGrossHoursWorked = (timeIn: string, timeOut: string): number => {
  const [inHours, inMinutes] = timeIn.split(':').map(Number)
  const [outHours, outMinutes] = timeOut.split(':').map(Number)
  
  const inTotalMinutes = inHours * 60 + inMinutes
  const outTotalMinutes = outHours * 60 + outMinutes
  
  let diffMinutes = outTotalMinutes - inTotalMinutes
  
  // Handle overnight shifts (next day)
  if (diffMinutes < 0) {
    diffMinutes += 24 * 60
  }
  
  return Math.round((diffMinutes / 60) * 100) / 100 // Round to 2 decimal places
}

/**
 * Calculates net hours worked with mandatory lunch deduction
 * 
 * Business Rule: Workers must take a 1-hour unpaid lunch break 
 * if working more than 4 hours in a shift.
 * 
 * @param timeIn Start time in HH:MM format (24-hour)
 * @param timeOut End time in HH:MM format (24-hour)
 * @returns Net billable hours (gross hours minus lunch deduction)
 */
export const calculateHoursWorked = (timeIn: string, timeOut: string): number => {
  const grossHours = calculateGrossHoursWorked(timeIn, timeOut)
  
  // Business Rule: Subtract 1 hour for mandatory lunch if working more than 4 hours
  const netHours = grossHours > 4 ? grossHours - 1 : grossHours
  return Math.max(0, netHours)
}

/**
 * Validates timesheet entry times according to business rules
 * @param timeIn Start time in HH:MM format
 * @param timeOut End time in HH:MM format
 * @returns Validation result with error message if invalid
 */
export const validateTimesheetEntry = (timeIn: string, timeOut: string): { valid: boolean; error?: string } => {
  if (timeIn === timeOut) {
    return { valid: false, error: 'Time in and time out cannot be the same' }
  }
  
  const hoursWorked = calculateHoursWorked(timeIn, timeOut)
  if (hoursWorked > 24) {
    return { valid: false, error: 'Hours worked cannot exceed 24 hours' }
  }
  
  if (hoursWorked < 0.5) {
    return { valid: false, error: 'Minimum work time is 30 minutes' }
  }
  
  return { valid: true }
}

/**
 * Estimates basic payroll cost for hours worked (regular time only)
 * 
 * Note: Overtime calculation removed - will be implemented later based on
 * work cycles, pay periods, and regulatory requirements.
 * 
 * @param hoursWorked Net hours worked (after lunch deduction)
 * @param hourlyRate Base hourly rate
 * @returns Estimated cost for regular hours only
 */
export const calculateBasicPayrollCost = (
  hoursWorked: number, 
  hourlyRate: number
): number => {
  const totalCost = hoursWorked * hourlyRate
  return Math.round(totalCost * 100) / 100 // Round to 2 decimal places
}

// TODO: Implement proper overtime calculation based on:
// - Work cycle patterns (weekly, bi-weekly, etc.)
// - State and federal overtime regulations  
// - Daily vs weekly overtime rules
// - Holiday and weekend multipliers
// - Union contract requirements 